import { GaContext } from '@/contexts/GaContext';
import { LobbyUiContext } from '@/contexts/LobbyUiContext';
import { getGAGameCodeSuffix } from '@/hooks/useGA';
import { FilterType } from '@/hooks/useLobbyUI';
import { CombineGameType, GameType } from '@/models/games/enums/GameType';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import 'swiper/css';
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react';
import { getLobbyStateHallGameTypeMap } from '../../selector';
import { IconWrapper } from './IconWrapper';
import './index.scss';

export const combineGameType = (source: Array<FilterType>) => {
    let arr = new Array<FilterType>();
    arr = [...source];
    // SicBo + ThaiHiLo
    arr = arr.map(gt =>
        (CombineGameType.Dice & gt) > 0 ? CombineGameType.Dice : gt
    );
    // TeenPatti2020 + AndarBahar
    arr = arr.map(gt =>
        (CombineGameType.Poker & gt) > 0 ? CombineGameType.Poker : gt
    );
    // Roulette + Ultra Roulette
    arr = arr.map(gt =>
        (CombineGameType.Roulette & gt) > 0 ? CombineGameType.Roulette : gt
    );
    //Game Show
    arr = arr.map(gt =>
        (CombineGameType.GameShow & gt) > 0 ? CombineGameType.GameShow : gt
    );
    //Blackjack
    arr = arr.map(gt =>
        (CombineGameType.Blackjack & gt) > 0 ? CombineGameType.Blackjack : gt
    );

    return arr;
};

export const GameTypeFilter = () => {
    const { t, i18n } = useTranslation();
    const [available, setAvailable] = useState<Array<FilterType>>([]);

    const hallGameTypeMap = useSelector(getLobbyStateHallGameTypeMap);

    const { gameFilter, setGameFilter } = useContext(LobbyUiContext);

    const [maskClassName, setMaskClassName] = useState('gameTypeRightMask');

    const swiperRef = useRef<SwiperRef>(null);

    // Update swiper when language changes
    useEffect(() => {
        if (
            swiperRef.current?.swiper !== undefined &&
            swiperRef.current?.swiper !== null
        )
            swiperRef.current.swiper.update();
    }, [i18n.language]);

    // Scroll to selected game type when filter changes
    useEffect(() => {
        const slideTimeout = setTimeout(() => {
            const index = available.findIndex(g => g === gameFilter);
            if (
                swiperRef.current?.swiper !== undefined &&
                swiperRef.current?.swiper !== null
            )
                swiperRef.current.swiper.slideTo(index == -1 ? 0 : index);
        }, 300);
        return () => {
            slideTimeout && clearTimeout(slideTimeout);
        };
    }, [available, gameFilter]);

    useEffect(() => {
        let gameTypes: Array<FilterType> = hallGameTypeMap.map(m => m.gameType);
        // combine game type logic
        gameTypes = combineGameType(gameTypes);
        const group = new Set(gameTypes);
        const result: Array<FilterType> = [-1, ...Array.from(group)];

        setAvailable(result);
        if (result.indexOf(gameFilter) < 0) {
            setGameFilter(-1);
        }

        // Set default landing page to Baccarat
        if (gameFilter < 0 && result.indexOf(GameType.Baccarat) >= 0) {
            setGameFilter(result.indexOf(GameType.Baccarat));
        }
    }, [hallGameTypeMap]);

    // Function to handle game filter click
    const handleGameFilterClick = (a: FilterType) => {
        setGameFilter(a);
    };

    // Handle swiper position and update mask classes
    const handleSwiperPositionChange = (sw: SwiperClass) => {
        if (sw.isEnd && sw.isBeginning) {
            setMaskClassName(''); // No mask needed when all slides are visible
        } else if (sw.isEnd) {
            setMaskClassName('gameTypeLeftMask'); // Left mask when at the end
        } else if (sw.isBeginning) {
            setMaskClassName('gameTypeRightMask'); // Right mask when at the beginning
        } else {
            setMaskClassName('gameTypeMask'); // Both masks when in the middle
        }
    };

    const { page } = useContext(GaContext);

    return (
        <Grid
            container
            flexWrap={'nowrap'}
            className={`game-type-filter-container ${maskClassName}`}
        >
            <Swiper
                ref={swiperRef}
                className="mySwiper"
                slidesPerView={'auto'}
                centeredSlides={true}
                centeredSlidesBounds
                direction="horizontal"
                touchStartForcePreventDefault={true}
                onReachBeginning={() => setMaskClassName('gameTypeRightMask')}
                onReachEnd={() => setMaskClassName('gameTypeLeftMask')}
                onResize={handleSwiperPositionChange}
                onSlideChange={handleSwiperPositionChange}
            >
                {available?.map((a, index) => (
                    <SwiperSlide key={`filter-game-type-${a}`} tabIndex={index}>
                        <Grid
                            item
                            className={clsx(
                                'game-type-filter',
                                gameFilter === a && 'selected'
                            )}
                            alignItems={'center'}
                        >
                            <Button
                                id={`${page}_${getGAGameCodeSuffix(a)}`}
                                onClick={() => handleGameFilterClick(a)}
                                className="filter-button my-font-16"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    transition: 'none',
                                }}
                            >
                                <IconWrapper gameType={a} />
                                {t(`betlog.gameCode.${a}`)}
                            </Button>
                        </Grid>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Grid>
    );
};
